import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSessionStore } from "../store/sessionStore";
import { getScenarioLibrary } from "../utils/apiUtil";
import { scenariosInProgress, type ScenarioTitle } from "./scenarioCrumbModel";
import MenuSurface, { MenuRow, MenuRule } from "./MenuSurface";

interface ScenarioCrumbProps {
  activeScenarioId: string | undefined;
  activeTitle: string;
  getToken: () => string | undefined;
}

const ScenarioCrumb: React.FC<ScenarioCrumbProps> = ({
  activeScenarioId,
  activeTitle,
  getToken,
}) => {
  const [open, setOpen] = useState(false);
  const [titles, setTitles] = useState<ScenarioTitle[]>([]);
  const [loaded, setLoaded] = useState(false);
  const navigate = useNavigate();
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const sessions = useSessionStore((s) => s.sessions);

  // Titles for scenarios other than the current one are not part of the chat
  // payload, so they are fetched on first open rather than on every chat load.
  // A failure is not surfaced: the rows still render from sessions alone, so
  // the switcher degrades to unnamed rather than to absent.
  const handleOpenChange = async (next: boolean) => {
    setOpen(next);
    if (!next || loaded) return;
    setLoaded(true);
    try {
      const library = await getScenarioLibrary(getToken());
      setTitles(library.map((s) => ({ id: s.id, title: s.title })));
    } catch {
      setTitles([]);
    }
  };

  const rows = scenariosInProgress(sessions, titles);

  const openScenario = (scenarioId: string) => {
    setOpen(false);
    if (scenarioId === activeScenarioId) return;
    const latest = sessions
      .filter((s) => s.scenarioId === scenarioId)
      .slice()
      .sort((a, b) => (a.lastModifiedAt < b.lastModifiedAt ? 1 : -1))[0];
    if (latest) navigate(`/${orgSlug}/chat/${latest.id}`);
  };

  return (
    <MenuSurface
      open={open}
      onOpenChange={handleOpenChange}
      align="left"
      triggerTitle={activeTitle}
      triggerClassName="flex items-center gap-1.5 px-2 py-1.5 rounded-md min-w-0 max-w-[300px] font-label text-[12.5px] font-semibold text-slate-400 hover:text-on-surface hover:bg-surface-container transition-colors"
      trigger={
        <>
          {/* flex-1 min-w-0 is load-bearing: a flex item defaults to
              min-width:auto, which for nowrap text resolves to the full
              unwrapped width, so the item never shrinks and truncate never
              engages. Same pattern as MenuRow's own label. */}
          <span className="flex-1 min-w-0 truncate">{activeTitle}</span>
          <span className="material-symbols-outlined text-[16px] text-slate-500 shrink-0">
            expand_more
          </span>
        </>
      }
    >
      {rows.map((row) => (
        <MenuRow
          key={row.id}
          icon={row.id === activeScenarioId ? "check" : undefined}
          active={row.id === activeScenarioId}
          meta={`${row.sessionCount} session${row.sessionCount === 1 ? "" : "s"}`}
          onClick={() => openScenario(row.id)}
        >
          {row.title}
        </MenuRow>
      ))}
      <MenuRule />
      {/* Starting a new scenario is a Library action: that is where scenarios
          are browsable with art, description and category. This is a door to
          it, not a create control. */}
      <MenuRow
        icon="grid_view"
        onClick={() => {
          setOpen(false);
          navigate(`/${orgSlug}/library`);
        }}
      >
        Browse library…
      </MenuRow>
    </MenuSurface>
  );
};

export default ScenarioCrumb;
